import React, { useMemo, useState } from 'react';
import { ArrowUpDownIcon, ExternalLinkIcon, SearchIcon } from 'lucide-react';
import { CommissionStep, Loan, Role } from '../types/commission';
import { commissionForLoan, formatCurrency, formatDate, totalCommission } from '../utils/commission';
interface LoanBreakdownTableProps {
  role: Role;
  /** Funded / closed units the role is paid on this period. */
  loans: Loan[];
  payingStep: CommissionStep;
  periodLabel: string;
  /** Hides interactive controls while the page is captured for PDF export. */
  hideSearch?: boolean;
}
type SortKey = 'fundedDate' | 'amount' | 'commission';
const INITIAL_ROWS = 12;
export function LoanBreakdownTable({
  role,
  loans,
  payingStep,
  periodLabel,
  hideSearch = false
}: LoanBreakdownTableProps) {
  const [query, setQuery] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('fundedDate');
  const [descending, setDescending] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const enriched = loans.map(loan => ({
      loan,
      commission: commissionForLoan(role, payingStep, loan)
    })).filter(({
      loan
    }) => {
      if (needle.length === 0) return true;
      return loan.borrower.toLowerCase().includes(needle) || loan.id.toLowerCase().includes(needle) || loan.product.toLowerCase().includes(needle);
    });
    return enriched.sort((a, b) => {
      const direction = descending ? -1 : 1;
      if (sortKey === 'amount') return (a.loan.amount - b.loan.amount) * direction;
      if (sortKey === 'commission') return (a.commission - b.commission) * direction;
      return a.loan.fundedDate.localeCompare(b.loan.fundedDate) * direction;
    });
  }, [loans, role, payingStep, query, sortKey, descending]);
  const visibleRows = showAll || hideSearch ? rows : rows.slice(0, INITIAL_ROWS);
  const totals = totalCommission(role, payingStep, rows.map(row => row.loan));
  const totalVolume = rows.reduce((sum, row) => sum + row.loan.amount, 0);
  function toggleSort(key: SortKey) {
    if (key === sortKey) {
      setDescending(value => !value);
      return;
    }
    setSortKey(key);
    setDescending(key !== 'fundedDate');
  }
  return <section aria-labelledby="loans-heading" className="rounded-xl border border-slate-200 bg-white shadow-card" data-id="element-71" data-mp="2zb6k57f5xy7c">
      <div className="flex flex-col gap-4 border-b border-slate-200 p-6 lg:flex-row lg:items-center lg:justify-between" data-id="element-72" data-mp="22ns6fi0w8fd0">
        <div data-id="element-73" data-mp="26lprq9rjmhb4">
          <h2 id="loans-heading" className="text-base font-bold text-slate-900" data-id="element-74" data-mp="2bmvrgrjmf4u5">
            {role.scope === 'Personal' ? 'Funded loans' : `${role.scope} closed units`} · {periodLabel}
          </h2>
          <p className="mt-0.5 text-sm text-slate-500" data-id="element-75" data-mp="1iy7y08opx95i">
            {role.model === 'bps' ? `Each unit pays ${payingStep.totalBps} BPS of the loan amount.` : payingStep.perUnit ? `Each unit pays ${formatCurrency(payingStep.perUnit)} flat.` : 'No incentive is earned at the current volume.'}
          </p>
        </div>

        <div className={`relative ${hideSearch ? 'hidden' : ''}`} data-id="element-76" data-mp="1bcm7g4ia344g">
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" data-id="element-77" data-mp="16eayrx3pyznt" />
          <input type="search" value={query} onChange={event => setQuery(event.target.value)} placeholder="Search borrower or loan #" aria-label="Search loans" className="w-72 rounded-lg border border-slate-300 py-2 pl-9 pr-3 text-sm text-slate-900 placeholder:text-slate-400" data-id="element-78" data-mp="xffq8ed7ddhc" />
        </div>
      </div>

      <div className="overflow-x-auto" data-id="element-79" data-mp="eo6gc3zismp8">
        <table className="w-full min-w-[640px] text-left text-sm" data-id="element-80" data-mp="enwrwku4inln">
          <caption className="sr-only" data-id="element-81" data-mp="9otot5xpergb">Loans paid on in {periodLabel}</caption>
          <thead data-id="element-82" data-mp="29rl4shx3smkd">
            <tr className="border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500" data-id="element-83" data-mp="29r2zkvsgqy0b">
              <th scope="col" className="px-6 py-3" data-id="element-84" data-mp="2dsogqtc15ygb">
                Borrower
              </th>
              <th scope="col" className="px-6 py-3" data-id="element-85" data-mp="20102oki80zwp">
                Loan #
              </th>
              <th scope="col" className="px-6 py-3" data-id="element-86" data-mp="24zl5mrouxxou">
                Product
              </th>
              <th scope="col" className="px-6 py-3" data-id="element-87" data-mp="1q2u4odzjcdx4">
                <SortButton label="Funded" active={sortKey === 'fundedDate'} onClick={() => toggleSort('fundedDate')} data-id="element-88" data-mp="1v2owqkv1r9jr" />
              </th>
              <th scope="col" className="px-6 py-3 text-right" data-id="element-89" data-mp="1h3yxozoo2zsr">
                <SortButton label="Loan amount" active={sortKey === 'amount'} onClick={() => toggleSort('amount')} align="right" data-id="element-90" data-mp="1m1qpzut84nxh" />
              </th>
              <th scope="col" className="px-6 py-3 text-right" data-id="element-91" data-mp="3gezzhmlrs9fq">
                <SortButton label="Commission" active={sortKey === 'commission'} onClick={() => toggleSort('commission')} align="right" data-id="element-92" data-mp="3qjq82bboibsx" />
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-93" data-mp="38qmbbq2t5jl">
            {visibleRows.length === 0 ? <tr data-id="element-94" data-mp="3ujqkjqf7wcxc">
                <td colSpan={6} className="px-6 py-14 text-center" data-id="element-95" data-mp="d4tmic1dau3w">
                  <p className="text-sm font-semibold text-slate-700" data-id="element-96" data-mp="d76cncha4as1">
                    {loans.length === 0 ? 'No closed units in this period' : 'No loans match your search'}
                  </p>
                  <p className="mt-1 text-sm text-slate-500" data-id="element-97" data-mp="m3nnsfpn88ns">
                    {loans.length === 0 ? 'Units appear here as soon as they close.' : 'Try a different borrower name or loan number.'}
                  </p>
                </td>
              </tr> : visibleRows.map(({
            loan,
            commission
          }) => <tr key={loan.id} className="transition hover:bg-slate-50" data-id="element-98" data-mp="mwgiqjvjtg72">
                  <th scope="row" className="px-6 py-4 font-semibold" data-id="element-99" data-mp="rv1g8bjf1lrt">
                    <a href={`#/loans/${loan.id}`} onClick={event => event.preventDefault()} aria-label={`Open loan page for ${loan.borrower}`} className="group inline-flex items-center gap-1.5 text-brand-700 underline decoration-brand-200 decoration-2 underline-offset-2 transition hover:text-brand-900 hover:decoration-brand-500" data-id="element-100" data-mp="wtctkw8x6ees">
                      {loan.borrower}
                      <ExternalLinkIcon className="h-3.5 w-3.5 opacity-0 transition group-hover:opacity-100" aria-hidden="true" data-id="element-101" data-mp="1kinuqeu6matg" />
                    </a>
                  </th>
                  <td className="px-6 py-4 tabular-nums text-slate-500" data-id="element-102" data-mp="1tkdj1bsrotn0">{loan.id}</td>
                  <td className="px-6 py-4 text-slate-700" data-id="element-103" data-mp="3e1mqqytc3v6g">{loan.product}</td>
                  <td className="px-6 py-4 tabular-nums text-slate-700" data-id="element-104" data-mp="3a016gseh0em8">{formatDate(loan.fundedDate)}</td>
                  <td className="px-6 py-4 text-right tabular-nums text-slate-900" data-id="element-105" data-mp="3506gqs6flv2r">{formatCurrency(loan.amount)}</td>
                  <td className="px-6 py-4 text-right font-bold tabular-nums text-slate-900" data-id="element-106" data-mp="302n4fxtansdg">
                    {formatCurrency(commission)}
                  </td>
                </tr>)}
          </tbody>
          {rows.length > 0 ? <tfoot data-id="element-107" data-mp="3nzke7k3ddny0">
              <tr className="border-t border-slate-200 bg-slate-50 text-sm" data-id="element-108" data-mp="3nz0ubxu5sphc">
                <th scope="row" colSpan={4} className="px-6 py-4 text-left font-bold text-slate-900" data-id="element-109" data-mp="3j0h8ryjh9fpi">
                  {rows.length} {rows.length === 1 ? role.unitNoun : role.unitNounPlural}
                </th>
                <td className="px-6 py-4 text-right font-bold tabular-nums text-slate-900" data-id="element-110" data-mp="fj8u1nk1qvz0">
                  {formatCurrency(totalVolume)}
                </td>
                <td className="px-6 py-4 text-right font-bold tabular-nums text-brand-800" data-id="element-111" data-mp="3jzh33lsqet0w">
                  {formatCurrency(totals.net)}
                </td>
              </tr>
              {totals.capped ? <tr className="bg-slate-50 text-xs" data-id="element-112" data-mp="3az2helbuvbwv">
                  <td colSpan={6} className="px-6 pb-4 text-right font-medium text-slate-500" data-id="element-113" data-mp="3f3a8gp5sd3y9">
                    {formatCurrency(totals.gross)} earned, capped at {formatCurrency(role.monthlyCap ?? 0)}
                  </td>
                </tr> : null}
            </tfoot> : null}
        </table>
      </div>

      {!hideSearch && rows.length > INITIAL_ROWS ? <div className="border-t border-slate-200 px-6 py-4" data-id="element-114" data-mp="2kalb1kfjza7">
          <button type="button" onClick={() => setShowAll(value => !value)} className="text-sm font-semibold text-brand-700 transition hover:text-brand-900" data-id="element-115" data-mp="2ic7y3rqryikz">
            {showAll ? 'Show fewer' : `Show all ${rows.length} units`}
          </button>
        </div> : null}
    </section>;
}
interface SortButtonProps {
  label: string;
  active: boolean;
  onClick: () => void;
  align?: 'left' | 'right';
}
function SortButton({
  label,
  active,
  onClick,
  align = 'left'
}: SortButtonProps) {
  return <button type="button" onClick={onClick} className={`inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider transition hover:text-brand-700 ${active ? 'text-brand-700' : 'text-slate-500'} ${align === 'right' ? 'flex-row-reverse' : ''}`} data-id="element-116" data-mp="2jtwvgok3d5i7">
      {label}
      <ArrowUpDownIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-117" data-mp="25vp2erq6eocb" />
    </button>;
}